import { useState, useEffect, useCallback, useRef } from 'react'
import { supabase } from './supabase'
import { saveReferenceCache, loadReferenceCache, clearReferenceCache } from './offlineStore'

const CACHE_KEY = 'price_groups'

/**
 * usePriceGroups — fetches price_groups + catalog_item_prices from Supabase.
 *
 * Returns:
 *  groups          — array of { id, name, zip_codes }
 *  overrides       — array of { id, item_id, group_id (null=Default), price, cost }
 *  loading
 *  error
 *  upsertGroup     — create or update a price group
 *  deleteGroup     — delete a price group (cascades overrides)
 *  upsertOverrides — save price overrides for one item (array of { group_id, price, cost })
 *  getGroupForZip  — (zipCode: string) => group | null
 *  getPriceForItem — (itemId, groupId) => { price, cost } or null
 *  reload
 */
export function usePriceGroups() {
    const [groups,    setGroups]    = useState([])
    const [overrides, setOverrides] = useState([])
    const [loading,   setLoading]   = useState(true)
    const [error,     setError]     = useState(null)
    const loadIdRef = useRef(0)

    const fetchAndApply = useCallback(async (id, silent) => {
        if (!silent) {
            setLoading(true)
        }
        setError(null)

        const cached = await loadReferenceCache(CACHE_KEY, null).catch(() => null)
        if (cached) {
            setGroups(cached.data.groups || [])
            setOverrides(cached.data.overrides || [])
            if (!silent) setLoading(false)
        }

        const [g, o] = await Promise.all([
            supabase.from('price_groups').select('*').order('name'),
            supabase.from('catalog_item_prices').select('*'),
        ])

        if (id !== loadIdRef.current) return

        if (g.error || o.error) {
            console.error('[priceGroups] fetch error:', g.error?.message || o.error?.message)
            setError(g.error?.message || o.error?.message)
            if (!cached) {
                setGroups([])
                setOverrides([])
            }
            if (!silent) setLoading(false)
            return
        }
        setGroups(g.data || [])
        setOverrides(o.data || [])
        await saveReferenceCache(CACHE_KEY, { groups: g.data || [], overrides: o.data || [] }, null).catch(() => {})
        if (!silent) setLoading(false)
    }, [])

    const load = useCallback(async () => {
        const id = ++loadIdRef.current
        await fetchAndApply(id, false)
    }, [fetchAndApply])

    const silentReload = useCallback(async () => {
        const id = ++loadIdRef.current
        await fetchAndApply(id, true)
    }, [fetchAndApply])

    useEffect(() => { load() }, [load])

    const upsertGroup = useCallback(async (group) => {
        const payload = { name: group.name.trim().toUpperCase(), zip_codes: group.zip_codes || [] }
        if (group.id) payload.id = group.id
        const { data, error: err } = await supabase
            .from('price_groups')
            .upsert(payload, { onConflict: 'id' })
            .select()
            .single()
        if (err) throw new Error(err.message)
        setGroups(prev => {
            const idx = prev.findIndex(g => g.id === data.id)
            if (idx === -1) return [...prev, data]
            const next = [...prev]; next[idx] = data; return next
        })
        await clearReferenceCache(CACHE_KEY, null).catch(() => {})
        return data
    }, [])

    const deleteGroup = useCallback(async (id) => {
        const { error: err } = await supabase.from('price_groups').delete().eq('id', id)
        if (err) throw new Error(err.message)
        setGroups(prev => prev.filter(g => g.id !== id))
        setOverrides(prev => prev.filter(o => o.group_id !== id))
        await clearReferenceCache(CACHE_KEY, null).catch(() => {})
    }, [])

    /**
     * Save price overrides for one catalog item.
     * rows = [{ group_id: uuid|null, price, cost }, ...]
     * Replaces all overrides for that item_id.
     */
    const upsertOverrides = useCallback(async (itemId, rows) => {
        // Delete existing overrides for this item
        await supabase.from('catalog_item_prices').delete().eq('item_id', itemId)
        if (!rows.length) {
            setOverrides(prev => prev.filter(o => o.item_id !== itemId))
            return
        }
        const payload = rows.map(r => ({
            item_id:  itemId,
            group_id: r.group_id || null,
            price:    Number(r.price),
            cost:     Number(r.cost),
        }))
        const { data, error: err } = await supabase
            .from('catalog_item_prices')
            .insert(payload)
            .select()
        if (err) throw new Error(err.message)
        setOverrides(prev => [
            ...prev.filter(o => o.item_id !== itemId),
            ...(data || []),
        ])
        await clearReferenceCache(CACHE_KEY, null).catch(() => {})
        return data
    }, [])

    /** Returns the best matching price group for a zip code, or null for Default. */
    const getGroupForZip = useCallback((zipCode) => {
        if (!zipCode) return null
        const zip = zipCode.trim()
        return groups.find(g => g.zip_codes.includes(zip)) || null
    }, [groups])

    /**
     * Returns the override price/cost for an item + group.
     * Falls back to null (caller should use item.price / item.cost as Default).
     */
    const getPriceForItem = useCallback((itemId, groupId) => {
        const override = overrides.find(o => o.item_id === itemId && o.group_id === (groupId || null))
        if (override) return { price: override.price, cost: override.cost }
        if (!groupId) return null
        return overrides.find(o => o.item_id === itemId && o.group_id === null) || null
    }, [overrides])

    return {
        groups, overrides, loading, error,
        upsertGroup, deleteGroup, upsertOverrides,
        getGroupForZip, getPriceForItem,
        reload: load,
        silentReload,
    }
}
